import React, { useState } from 'react';
import Box from '@mui/material/Box';
import { DrawerV2 } from '../atoms/DrawerV2';
import { ButtonV2 } from '../atoms/ButtonV2';

/**
 * Drawer de formulaire du Design System V2 : `DrawerV2` + pied standardisé (paire Annuler /
 * Enregistrer) + gestion de l'état `submitting` pendant `onSubmit`.
 *
 * **Agnostique des toasts** : `ui.shared` n'importe pas le `toastify` applicatif. `onSubmit` (côté
 * app) effectue la mutation, le `notify` et la fermeture ; `FormDrawer` ne fait que désactiver le
 * bouton et afficher le spinner pendant la promesse. Factorise les pieds Annuler/Enregistrer
 * dupliqués dans chaque drawer.
 */
export type FormDrawerProps = {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  width?: number;
  children: React.ReactNode;
  /** Soumission (peut être asynchrone) — l'état `loading` du bouton couvre la durée de la promesse. */
  onSubmit: () => void | Promise<void>;
  submitLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  submitDisabled?: boolean;
  submitVariant?: 'primary' | 'danger';
  submitIcon?: React.ReactNode;
  /** Spinner piloté en plus de l'état interne (ex. `loading` d'une mutation Apollo). */
  loading?: boolean;
  submitAriaLabel?: string;
  /** Actions additionnelles alignées à gauche du pied (ex. « Révoquer »). */
  footerStart?: React.ReactNode;
};

export const FormDrawer = ({
  open,
  onClose,
  title,
  subtitle,
  width,
  children,
  onSubmit,
  submitLabel = 'Enregistrer',
  cancelLabel = 'Annuler',
  submitDisabled,
  submitVariant = 'primary',
  submitIcon,
  loading,
  submitAriaLabel,
  footerStart,
}: FormDrawerProps) => {
  const [submitting, setSubmitting] = useState(false);
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmit();
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      width={width}
      footer={
        <>
          {footerStart ? <Box sx={{ marginRight: 'auto' }}>{footerStart}</Box> : null}
          <ButtonV2 variant="secondary" onClick={onClose}>
            {cancelLabel}
          </ButtonV2>
          <ButtonV2
            variant={submitVariant}
            onClick={handleSubmit}
            disabled={submitDisabled}
            loading={submitting || loading}
            startIcon={submitIcon}
            ariaLabel={submitAriaLabel}
          >
            {submitLabel}
          </ButtonV2>
        </>
      }
    >
      {children}
    </DrawerV2>
  );
};

export default FormDrawer;
